import { useState } from 'react';

import type { UserDetail } from '@solar/api-client';
import { ApiError } from '@solar/api-client';
import { ErrorBox, ROLE_LABEL, Spinner, formatPhone, useAsync } from '@solar/ui';

import { api } from '../api';
import { ExcelButton } from '../ExcelButton';
import { Modal } from '../Modal';

/**
 * 계정 관리 > 사용자 — 레거시 WebService/auth/user 파리티.
 *
 * 역할 매트릭스(누가 어떤 역할을 만들 수 있는지)와 회사 격리는 서버가
 * 강제한다 — 여기서는 전체 역할을 보여주고 403/422 메시지를 그대로 표시한다.
 * 비밀번호 규칙(9~18자 영문+숫자+특수문자)도 서버 검증(422) 메시지에 맡긴다.
 */

const SIZE = 30;

const ROLES = Object.entries(ROLE_LABEL).map(([value, label]) => ({
  value: Number(value),
  label,
}));

function StatusBadge({ status }: { status: string | null }) {
  if (status === 'stop') return <span className="badge stop">정지</span>;
  return <span className="badge">정상</span>;
}

/** 사용자 엑셀 버튼 노출 역할 — 서버 USER_EXCEL_ROLES(1,2,3,5)와 동일. */
const USER_EXCEL_ROLES = [1, 2, 3, 5];

export function UserManage({ viewerRole }: { viewerRole: number }) {
  const [role, setRole] = useState<number | ''>('');
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const list = useAsync(
    () =>
      api.listUsers({
        role: role === '' ? undefined : role,
        keyword: applied,
        page,
        size: SIZE,
      }),
    [role, applied, page],
  );

  const companies = useAsync(() => api.listCompanies({ size: 200 }), []);
  const companyName = (id: number | null) =>
    companies.data?.items.find((c) => c.id === id)?.name ?? (id === null ? '-' : `#${id}`);

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          <select
            value={role}
            onChange={(e) => {
              setRole(e.target.value === '' ? '' : Number(e.target.value));
              setPage(1);
            }}
          >
            <option value="">전체 권한</option>
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>

          <form
            className="search"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(keyword.trim());
              setPage(1);
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="이름 · 아이디"
            />
            <button type="submit">검색</button>
          </form>
          <button type="button" className="primary create" onClick={() => setCreateOpen(true)}>
            등록
          </button>

          {/* 레거시 UserExcel — 현재 역할/검색 필터 그대로 내려받는다. */}
          {USER_EXCEL_ROLES.includes(viewerRole) && (
            <ExcelButton
              label="사용자 엑셀"
              fallback="users.xlsx"
              fetcher={() =>
                api.usersExcel({ role: role === '' ? undefined : role, keyword: applied })
              }
            />
          )}
        </div>

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th>아이디</th>
                <th>이름</th>
                <th>권한</th>
                <th>업체명</th>
                <th>상태</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    조건에 맞는 사용자가 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((u) => (
                <tr
                  key={u.id}
                  className={u.id === selected ? 'selected' : ''}
                  onClick={() => setSelected(u.id)}
                >
                  <td>{u.user_id}</td>
                  <td>{u.name ?? '-'}</td>
                  <td>{ROLE_LABEL[u.type] ?? u.type}</td>
                  <td>{companyName(u.build_company_id)}</td>
                  <td>
                    <StatusBadge status={u.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>
      </section>

      <aside className="admin__aside">
        {selected === null ? (
          <p className="muted">사용자를 선택하세요.</p>
        ) : (
          <UserEdit
            key={selected}
            userId={selected}
            onClose={() => setSelected(null)}
            onChanged={() => list.reload()}
          />
        )}
      </aside>

      {createOpen && (
        <UserCreateModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            list.reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * 자치구 셀렉트 — 구 관리자(role 7) 계정의 담당 자치구 지정용.
 *
 * 저장된 addr_gu_id 만으로는 소속 시/도를 알 수 없어, 시/도 목록을 받은 뒤
 * 시별 자치구를 모두 불러와 optgroup 하나짜리 셀렉트로 합친다. 시/도는
 * 실질적으로 서울 한 곳이라 요청 수 부담이 없다.
 */
function GuSelect({
  value,
  onChange,
}: {
  value: number | '';
  onChange: (value: number | '') => void;
}) {
  const groups = useAsync(async () => {
    const siList = await api.listAddrSi();
    const guLists = await Promise.all(siList.map((si) => api.listAddrGu(si.id)));
    return siList.map((si, index) => ({ si, gus: guLists[index] ?? [] }));
  }, []);

  if (groups.error) return <ErrorBox message={groups.error} onRetry={groups.reload} />;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
    >
      <option value="">자치구 미지정</option>
      {(groups.data ?? []).map(({ si, gus }) => (
        <optgroup key={si.id} label={si.name}>
          {gus.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/** 팀 셀렉트 — 대상 사용자의 회사 소속 팀만 보여준다. */
function TeamSelect({
  companyId,
  value,
  onChange,
}: {
  companyId: number | null;
  value: number | '';
  onChange: (value: number | '') => void;
}) {
  const teams = useAsync(
    () => (companyId === null ? Promise.resolve(null) : api.listTeams(companyId)),
    [companyId],
  );

  if (companyId === null) return <span className="muted">소속 회사 없음</span>;
  if (teams.error) return <ErrorBox message={teams.error} onRetry={teams.reload} />;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
    >
      <option value="">소속 팀 없음</option>
      {(teams.data ?? []).map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  );
}

function UserEdit({
  userId,
  onClose,
  onChanged,
}: {
  userId: number;
  onClose: () => void;
  onChanged: () => void;
}) {
  const detail = useAsync(() => api.getUser(userId), [userId]);

  if (detail.loading) return <Spinner />;
  if (detail.error) return <ErrorBox message={detail.error} onRetry={detail.reload} />;
  if (!detail.data) return null;

  return (
    <UserEditForm
      key={detail.data.id}
      user={detail.data}
      onClose={onClose}
      onChanged={() => {
        detail.reload();
        onChanged();
      }}
    />
  );
}

function UserEditForm({
  user,
  onClose,
  onChanged,
}: {
  user: UserDetail;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [name, setName] = useState(user.name ?? '');
  const [email, setEmail] = useState(user.email ?? '');
  const [contact, setContact] = useState(user.contact ?? '');
  const [type, setType] = useState(user.type);
  const [status, setStatus] = useState(user.status ?? 'normal');
  const [teamId, setTeamId] = useState<number | ''>(user.build_team_id ?? '');
  const [guId, setGuId] = useState<number | ''>(user.addr_gu_id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // 비밀번호 변경 — 규칙(9~18자 영문+숫자+특수문자)은 서버 422 메시지로 표시.
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwOk, setPwOk] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) {
      setError('이름을 입력해 주세요.');
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await api.updateUser(user.id, {
        name: name.trim(),
        email: email.trim() || undefined,
        contact: contact.trim() || undefined,
        type,
        status,
        build_team_id: teamId === '' ? null : teamId,
        // 자치구는 구 관리자(7)일 때만 보낸다 — 다른 역할은 건드리지 않는다.
        addr_gu_id: type === 7 ? (guId === '' ? null : guId) : undefined,
      });
      setOk('저장했습니다.');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  async function changePassword() {
    if (password !== passwordConfirm) {
      setPwError('비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    setPwBusy(true);
    setPwError(null);
    setPwOk(null);
    try {
      await api.changePassword(user.id, password);
      setPassword('');
      setPasswordConfirm('');
      setPwOk('비밀번호를 변경했습니다.');
    } catch (err) {
      setPwError(err instanceof ApiError ? err.message : '비밀번호를 변경하지 못했습니다.');
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>
          {user.name ?? user.user_id} <StatusBadge status={user.status} />
        </h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <section className="panel form-grid">
        <label className="f wide">
          아이디
          <input value={user.user_id} readOnly disabled />
        </label>
        <label className="f wide">
          이름 *
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="f wide">
          Email
          <input value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="f wide">
          연락처
          <input
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder={formatPhone(user.contact)}
          />
        </label>
        <label className="f wide">
          권한
          <select value={type} onChange={(e) => setType(Number(e.target.value))}>
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <label className="f wide">
          상태
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="normal">정상</option>
            <option value="stop">정지</option>
          </select>
        </label>
        <label className="f wide">
          설치 팀
          <TeamSelect companyId={user.build_company_id} value={teamId} onChange={setTeamId} />
        </label>
        {/* 구 관리자(7)는 담당 자치구를 지정한다 — PATCH /users/{id} 의 addr_gu_id. */}
        {type === 7 && (
          <label className="f wide">
            담당 자치구
            <GuSelect value={guId} onChange={setGuId} />
          </label>
        )}
      </section>

      {error && <p className="error">{error}</p>}
      {ok && <p className="ok">{ok}</p>}
      <div className="row form-actions">
        <button type="button" className="primary" disabled={busy} onClick={() => void save()}>
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>

      <h3 className="section">비밀번호 변경</h3>
      <section className="panel form-grid">
        <label className="f wide">
          비밀번호 *
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="9~18자 영문+숫자+특수문자(!@#$%^*+=-)"
          />
        </label>
        <label className="f wide">
          비밀번호 확인 *
          <input
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
          />
        </label>
      </section>
      {pwError && <p className="error">{pwError}</p>}
      {pwOk && <p className="ok">{pwOk}</p>}
      <div className="row form-actions">
        <button
          type="button"
          disabled={pwBusy || password.length === 0}
          onClick={() => void changePassword()}
        >
          {pwBusy ? '변경 중…' : '비밀번호 변경'}
        </button>
      </div>
    </div>
  );
}

function UserCreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const companies = useAsync(() => api.listCompanies({ size: 200 }), []);

  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState('');
  const [type, setType] = useState(4);
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [teamId, setTeamId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!userId.trim() || !password || !name.trim()) {
      setError('아이디 · 비밀번호 · 이름을 입력해 주세요.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createUser({
        user_id: userId.trim(),
        password,
        name: name.trim(),
        email: email.trim() || undefined,
        contact: contact.trim() || undefined,
        type,
        build_company_id: companyId === '' ? undefined : companyId,
        build_team_id: teamId === '' ? undefined : teamId,
      });
      onCreated();
    } catch (err) {
      // 아이디 min:5, 비밀번호 규칙 등 서버 422/409 메시지를 그대로 보여준다.
      setError(err instanceof ApiError ? err.message : '등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="사용자 등록" onClose={onClose}>
      <label className="labeled">
        아이디 *
        <input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="5자 이상" />
      </label>
      <label className="labeled">
        비밀번호 *
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="9~18자 영문+숫자+특수문자(!@#$%^*+=-)"
        />
      </label>
      <label className="labeled">
        비밀번호 확인 *
        <input
          type="password"
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
        />
      </label>
      <label className="labeled">
        이름 *
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="labeled">
        Email
        <input value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label className="labeled">
        연락처
        <input value={contact} onChange={(e) => setContact(e.target.value)} />
      </label>
      <label className="labeled">
        권한 *
        <select value={type} onChange={(e) => setType(Number(e.target.value))}>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </label>
      <label className="labeled">
        설치 회사
        <select
          value={companyId}
          onChange={(e) => {
            setCompanyId(e.target.value === '' ? '' : Number(e.target.value));
            setTeamId('');
          }}
        >
          <option value="">소속 없음</option>
          {(companies.data?.items ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      {companyId !== '' && (
        <label className="labeled">
          설치 팀
          <TeamSelect companyId={companyId} value={teamId} onChange={setTeamId} />
        </label>
      )}

      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '등록 중…' : '등록'}
        </button>
      </div>
    </Modal>
  );
}
